import React, { useEffect, useState, useRef } from "react"
import Pagination from "../../components/Pagination"
import { useHistory } from "react-router-dom"
import axios from "axios"
import Spinner from "../../components/Spinner"
import api, { fetchAll, BASE_URL } from "../../utils/api"
import DashboardHeader from "../../components/DashboardHeader"
import FilterWithSearchAndRegion from "../../components/Filters/FilterWithSearchAndRegion"
import RenderNoFarm from "../../components/NoData/RenderNoFarm"
import { FiEye, FiSearch } from "react-icons/fi"
import { RxPencil1 } from "react-icons/rx"
import { Tooltip } from "../../layout/sidebar"
import { IoArrowBack, IoArrowForward } from "react-icons/io5"
import { FiDownload } from "react-icons/fi"
import FarmerDetailDrawer from "../../components/Drawers/FarmerDetailDrawer"
import { ErrorNotification } from "../../utils"
import FarmLeafletMap from "../../components/FarmLeafletMap"
import DeleteModal from "../../components/DeleteModal"
import { useLocations } from "../../hooks/useLocations"

const Index = () => {
  const history = useHistory()
  const { regionNames } = useLocations()
  const [farms, setFarms] = useState([])
  const [loading, setLoading] = useState(true)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [selecteduser, setSelectedUser] = useState(null)
  const [search, setSearch] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [view, setView] = useState("list") // list | map
  const [isOpen, setIsOpen] = useState(false)
  const [filteredFarms, setFilteredFarms] = useState([])
  const [isFiltered, setIsFiltered] = useState(false)
  const [itemsPerPage] = useState(10)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [selectedFarmId, setSelectedFarmId] = useState(null)

  const initialState = {
    region: "",
    district: "",
    community: "",
  }
  const [states, setState] = useState(initialState)

  const filterFarms = (searchTerm = search) => {
    let filteredData = farms

    // Filter by search term
    if (searchTerm && searchTerm.trim()) {
      filteredData = filteredData.filter(farm =>
        farm?.farm_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        farm?.farmer_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        farm?.ownership_type?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      )
    }

    // Filter by region
    if (states.region && states.region !== "Select Region") {
      filteredData = filteredData.filter(farm =>
        farm?.region?.toLowerCase()?.includes(states.region?.toLowerCase())
      )
    }

    // Filter by district
    if (states.district && states.district !== "Select District") {
      filteredData = filteredData.filter(farm =>
        farm?.district?.toLowerCase()?.includes(states.district?.toLowerCase())
      )
    }

    // Filter by community
    if (states.community && states.community !== "Select Community") {
      filteredData = filteredData.filter(farm =>
        farm?.community?.toLowerCase()?.includes(states.community?.toLowerCase())
      )
    }

    setFilteredFarms(filteredData)
    setIsFiltered(true)
    setCurrentPage(1)
  }

  const getAllFarms = async () => {
    setLoading(true)
    try {
      const data = await fetchAll(`farmer-farms/`)
      setFarms(data || [])
    } catch (error) {
      ErrorNotification("Failed to get farms. Please try again")
    } finally {
      setLoading(false)
    }
  }

  function handleChange(evt) {
    const value = evt.target.value
    setState({
      ...states,
      [evt.target.name]: value,
    })
  }

  // Update pagination to use filtered data when available
  const dataToShow = isFiltered ? filteredFarms : farms
  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentItems = dataToShow.slice(indexOfFirstItem, indexOfLastItem)
  const totalPages = Math.ceil(dataToShow.length / itemsPerPage);

  const resetFilters = () => {
    setSearch("")
    setState(initialState)
    setFilteredFarms([])
    setIsFiltered(false)
    setCurrentPage(1)
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  useEffect(() => {
    getAllFarms()
  }, [])

  const handleRowClick = (row) => {
    setSelectedUser({
      ...row,
      my_farms: [{
        ...row,
        farm_name: row?.farm_name,
        farm_size: row?.farm_size,
        primary_crop: row?.primary_crop,
        region: row?.region,
        district: row?.district,
        community: row?.community,
        ownership_type: row?.ownership_type,
        images: row?.images,
        longitude: row.longitude,
        latitude: row.latitude,
      }]
    })
    setIsOpen(true)
  }

  const handleDeleteFarm = async () => {
    if (!selecteduser) return
    setDeleteLoading(true)
    try {
      const farmerId = selecteduser.farmer_id || selecteduser.farmer?.id || selecteduser.id
      const farmId = selecteduser.id || selecteduser.farm_id
      await api.delete(`farmers/${farmerId}/delete_farm/?farm_id=${farmId}`)
      setShowConfirmModal(false)
      setSelectedUser(null)
      await getAllFarms()
    } catch (error) {
      ErrorNotification("Failed to delete farm. Please try again.")
    } finally {
      setDeleteLoading(false)
    }
  }

  const exportToGeoJSON = () => {
    const dataToExport = isFiltered ? filteredFarms : farms
    const features = dataToExport.map(farm => {
      let geometry = null
      if (farm?.is_mapped && farm?.mapped_coordinates) {
        const coords = farm?.mapped_coordinates
        const points = []
        const pointKeys = Object.keys(coords).filter(key => /^point[A-Z]$/.test(key)).sort()
        pointKeys.forEach(key => {
          const point = coords[key]
          if (point) {
            const lat = parseFloat(point?.latitude)
            const lng = parseFloat(point?.longitude)
            if (!isNaN(lat) && !isNaN(lng)) points.push([lng, lat])
          }
        })
        if (points.length >= 3) points.push(points[0])
        if (points.length >= 4) geometry = { type: "Polygon", coordinates: [points] }
      }
      if (!geometry && farm?.latitude && farm?.longitude) {
        geometry = { type: "Point", coordinates: [parseFloat(farm?.longitude), parseFloat(farm?.latitude)] }
      }
      return {
        type: "Feature",
        properties: {
          id: farm?.id,
          farm_name: farm?.farm_name || "Unknown Farm",
          farmer_name: `${farm?.farmer?.firstname || ''} ${farm?.farmer?.lastname || ''}`.trim() || "Unknown Farmer",
          region: farm?.region || "",
          district: farm?.district || "",
          community: farm?.community || "",
          primary_crop: farm?.primary_crop || "",
          farm_size: farm?.farm_size || "",
          ownership_type: farm?.ownership_type || "",
          is_mapped: farm?.is_mapped || false
        },
        geometry: geometry
      }
    }).filter(f => f.geometry !== null)

    const geoJSON = {
      type: "FeatureCollection",
      features: features,
      properties: {
        name: "Farm Map Export",
        export_date: new Date().toISOString().split('T')[0],
        total_farms: features.length
      }
    }
    const dataStr = JSON.stringify(geoJSON, null, 2)
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr)
    const exportFileDefaultName = `farm_export_${new Date().toISOString().split('T')[0]}.geojson`
    const linkElement = document.createElement('a')
    linkElement.setAttribute('href', dataUri)
    linkElement.setAttribute('download', exportFileDefaultName)
    linkElement.click()
  }

  return (
    <>
      <FarmerDetailDrawer
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        farmer={selecteduser}
        showOnlyFarm
      />

      {showConfirmModal && <DeleteModal
        handleDelete={handleDeleteFarm}
        loading={deleteLoading}
        title="farm"
        setShowConfirmModal={setShowConfirmModal}
        showConfirmModal={showConfirmModal}
      />}

      {view === "map" ? (
        <RenderMap
          farms={isFiltered ? filteredFarms : farms}
          selectedFarmId={selectedFarmId}
          onFarmSelect={setSelectedFarmId}
          onMarkerClick={setSelectedFarmId}
          view={view}
          setView={setView}
          handleChange={handleChange}
          states={states}
          search={search}
          setSearch={setSearch}
          onSearch={(searchTerm) => {
            setSearch(searchTerm)
            filterFarms(searchTerm)
          }}
          suggestions={farms.map(farm => farm?.farm_name)}
          resetFilters={resetFilters}
          regionNames={regionNames}
        />
      ) : (
        <div className="w-full lg:px-10 px-2 h-full py-10 space-y-4">
          <DashboardHeader label="Farms" />
          <div className="flex flex-col gap-4">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1">
                <FilterWithSearchAndRegion
                  placeholder="Search farm"
                  suggestions={farms.map(farm => farm?.farm_name)}
                  onSearch={(searchTerm) => {
                    setSearch(searchTerm)
                    filterFarms(searchTerm)
                  }}
                  view={view}
                  setView={setView}
                  handleChange={handleChange}
                  search={search}
                  setSearch={setSearch}
                />
              </div>
              {dataToShow?.length > 0 && (
                <div className="flex gap-2">
                  <button
                    onClick={exportToGeoJSON}
                    className="flex items-center gap-2 px-4 py-2 bg-yellowcolor hover:bg-yellowcolor/80 text-black rounded-md text-sm font-medium transition-colors"
                  >
                    <FiDownload size={16} />
                    Export GeoJSON
                  </button>
                </div>
              )}
            </div>
            {(search || states.region || states.district || states.community) && (
              <div className="flex justify-end">
                <button
                  onClick={resetFilters}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm"
                >
                  Reset Filters
                </button>
              </div>
            )}
          </div>

          {loading && dataToShow?.length === 0 ? (
            <div className="h-96 grid items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <>
              <div className="my-5">
                {!loading && dataToShow?.length > 0 ? (
                  <div className="flex flex-col">
                    <div className="-m-1.5 overflow-x-auto">
                      <div className="p-1.5 min-w-full inline-block align-middle">
                        <div className="bg-white dark:bg-dark border dark:border-border-dark dark:text-white bg-clip-border rounded-lg shadow-sm overflow-hidden">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-border-dark">
                            <thead className="bg-transparent dark:bg-secondaryDark">
                              <tr>
                                <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Farm name</th>
                                <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Farm Owner</th>
                                <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Primary Commodity</th>
                                <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Farm Type</th>
                                <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Is Mapped</th>
                                <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Land Size</th>
                                <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Region</th>
                                <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Community</th>
                                <th className="px-6 py-3 text-end text-sm font-light text-gray-500 capitalize">Action</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y bg-white divide-gray-200 dark:bg-dark dark:divide-border-dark">
                              {currentItems?.map((data) => (
                                <tr key={data.id} className="text-[13px]">
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-800 dark:text-neutral-200 flex flex-row items-center gap-2">
                                    {data?.images?.length > 0 ? (
                                      <div className="w-10 h-10 overflow-hidden">
                                        <img
                                          src={data?.images[0].image}
                                          alt=""
                                          className="w-full h-full rounded-full"
                                        />
                                      </div>
                                    ) : (
                                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-200">
                                        <p className="text-sm uppercase font-bold text-gray-600">
                                          {data?.farm_name?.charAt(0)}
                                        </p>
                                      </div>
                                    )}
                                    {data?.farm_name || "N/A"}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {`${data?.farmer?.firstname || ''} ${data?.farmer?.lastname || ''}`}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {data?.primary_crop || "N/A"}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {data?.ownership_type || "N/A"}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {data?.is_mapped ? "Yes" : "No"}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {data?.farm_size || "N/A"}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {data?.region || "N/A"}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {data?.community || "N/A"}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-end">
                                    <div className="flex flex-row items-center justify-end gap-4">
                                      <Tooltip text="View" position="top">
                                        <button onClick={() => handleRowClick(data)}>
                                          <FiEye size={16} />
                                        </button>
                                      </Tooltip>
                                      <Tooltip text="Update" position="top">
                                        <button onClick={() => history.push('/admin/updatefarm', { farm: data })}>
                                          <RxPencil1 size={16} />
                                        </button>
                                      </Tooltip>
                                      <Tooltip text="Delete" position="top">
                                        <button onClick={() => {
                                          setSelectedUser(data)
                                          setShowConfirmModal(true)
                                        }}>
                                          <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M18 4.98307C15.225 4.70807 12.4333 4.56641 9.65 4.56641C8 4.56641 6.35 4.64974 4.7 4.81641L3 4.98307" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M7.58398 4.14102L7.76732 3.04935C7.90065 2.25768 8.00065 1.66602 9.40898 1.66602H11.5923C13.0007 1.66602 13.109 2.29102 13.234 3.05768L13.4173 4.14102" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M16.2077 7.61523L15.666 16.0069C15.5744 17.3152 15.4993 18.3319 13.1743 18.3319H7.82435C5.49935 18.3319 5.42435 17.3152 5.33268 16.0069L4.79102 7.61523" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M9.10742 13.75H11.8824" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M8.41602 10.416H12.5827" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                          </svg>
                                        </button>
                                      </Tooltip>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <RenderNoFarm />
                )}
              </div>
            </>
          )}
        </div>
      )}
    </>
  )
}

const RenderMap = ({ farms, selectedFarmId, onMarkerClick, view, setView, handleChange, states, resetFilters, search, setSearch, onSearch, suggestions, regionNames }) => (
  <div className="relative w-full h-[calc(100vh-100px)]">
    <div className="absolute inset-0 rounded-lg overflow-hidden border border-gray-200 dark:border-border-dark shadow-lg">
      <FarmLeafletMap farms={farms} selectedFarmId={selectedFarmId} onMarkerClick={onMarkerClick} />
    </div>
    <div className="absolute top-4 left-4 right-4 z-[1000] flex flex-col md:flex-row gap-4 bg-white/80 dark:bg-dark/80 backdrop-blur-md p-4 rounded-lg shadow-md border border-gray-200 dark:border-border-dark">
      <div className="flex gap-2">
        <button onClick={() => setView("map")} className={`p-2 rounded ${view === "map" ? "bg-yellowcolor text-black" : "bg-white dark:bg-secondaryDark text-gray-600"}`}>Map</button>
        <button onClick={() => setView("list")} className={`p-2 rounded ${view === "list" ? "bg-yellowcolor text-black" : "bg-white dark:bg-secondaryDark text-gray-600"}`}>List</button>
      </div>
      <div className="flex-1 flex flex-col sm:flex-row gap-2">
        <select name="region" value={states.region} onChange={handleChange} className="p-2 rounded border border-gray-200 dark:bg-dark dark:border-border-dark dark:text-white flex-1">
          <option value="">Select Region</option>
          {(regionNames || []).map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        {(states.region || search) && (
          <button onClick={resetFilters} className="px-4 py-2 bg-gray-500 text-white rounded">Reset</button>
        )}
      </div>
    </div>
  </div>
)

export default Index
